import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import STRINGS from "../../Constants/Strings";
import { isValidPostalCode } from "./modalUtils";
import { isValidPhoneNumber } from "./utils";

export const EditEmploymentModal = ({
  showEmploymentEditModal,
  handleEmploymentModalClose,
  applicant,
  handleEmploymentSaveClick,
  formatPhoneNumber,
  employmentAndIncomeReducer,
  occupationCodes,
  provinceList,
}) => {
  const [employmentFormData, setEmploymentFormData] = useState({
    employmentStatus: applicant?.employment?.employmentStatus || "",
    occupationCode: applicant?.employment?.occupationCode || "",
    jobTitle: applicant?.employment?.jobTitle || "",
    employerPosition: applicant?.employment?.employerPosition || "",
    currentEmployer: applicant?.employment?.currentEmployer || "",
    employerLine1: applicant?.employment?.employerLine1 || "",
    employerCity: applicant?.employment?.employerCity || "",
    employerProv: applicant?.employment?.employerProv || "",
    employerPc: applicant?.employment?.employerPc || "",
    employerPhone:
      applicant?.employment?.employerPhoneArea +
      applicant?.employment?.employerPhone,
    employerSalary: applicant?.employment?.employerSalary || "0",
    incomeType: applicant?.employment?.incomeType || "",
  });

  const [employmentFormErrors, setEmploymentFormErrors] = useState({
    employmentStatus: "",
    occupationCode: "",
    jobTitle: "",
    employerPosition: "",
    currentEmployer: "",
    employerLine1: "",
    employerCity: "",
    employerProv: "",
    employerPc: "",
    employerPhone: "",
    employerSalary: "",
    incomeType: "",
  });

  useEffect(() => {
    setEmploymentFormData({
      employmentStatus: applicant?.employment?.employmentStatus || "",
      occupationCode: applicant?.employment?.occupationCode || "",
      jobTitle: applicant?.employment?.jobTitle || "",
      employerPosition: applicant?.employment?.employerPosition || "",
      currentEmployer: applicant?.employment?.currentEmployer || "",
      employerLine1: applicant?.employment?.employerLine1 || "",
      employerCity: applicant?.employment?.employerCity || "",
      employerProv: applicant?.employment?.employerProv || "",
      employerPc: applicant?.employment?.employerPc || "",
      employerPhone:
        applicant?.employment?.employerPhoneArea +
        applicant?.employment?.employerPhone,
      employerSalary: applicant?.employment?.employerSalary || "0",
      incomeType: applicant?.employment?.incomeType || "",
    });
  }, [applicant]);

  const handleEmploymentInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "employerPosition") {
      setEmploymentFormData((prev) => ({
        ...prev,
        [name]: value,
        jobTitle: "",
      }));
    } else if (name === "jobTitle") {
      const alphanumericValue = value.replace(/[^a-zA-Z0-9\s]/g, "");
      setEmploymentFormData((prev) => ({ ...prev, [name]: alphanumericValue }));
    } else if (name === "employerLine1") {
      const validAddressValue = value.replace(/[^a-zA-Z0-9\s\-.,#']/g, "");
      setEmploymentFormData((prev) => ({ ...prev, [name]: validAddressValue }));
    } else if (name === "employerCity") {
      const validCityValue = value.replace(/[^a-zA-Z\s\-.']/g, "");
      setEmploymentFormData((prev) => ({ ...prev, [name]: validCityValue }));
    } else if (name === "employerPhone") {
      let digitsOnly = value.replace(/\D/g, "");
      if (
        value.startsWith("+1") &&
        digitsOnly.startsWith("1") &&
        digitsOnly.length > 1
      ) {
        digitsOnly = digitsOnly.substring(1);
      }
      if (digitsOnly.length <= 10) {
        setEmploymentFormData((prev) => ({ ...prev, [name]: digitsOnly }));
      }
    } else if (name === "employerPc") {
      let formattedValue = value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 6);
      if (formattedValue.length > 3) {
        formattedValue =
          formattedValue.slice(0, 3) + " " + formattedValue.slice(3);
      }
      setEmploymentFormData((prev) => ({ ...prev, [name]: formattedValue }));
    } else if (name === "employerSalary") {
      const numericValue = value.replace(/[^\d.]/g, "");
      const parts = numericValue.split(".");
      const formattedValue =
        parts.length > 2
          ? parts[0] + "." + parts.slice(1).join("")
          : numericValue;
      setEmploymentFormData((prev) => ({ ...prev, [name]: formattedValue }));
    } else {
      setEmploymentFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = () => {
    const errors = {
      employmentStatus: "",
      occupationCode: "",
      jobTitle: "",
      employerPosition: "",
      currentEmployer: "",
      employerLine1: "",
      employerCity: "",
      employerProv: "",
      employerPc: "",
      employerPhone: "",
      employerSalary: "",
      incomeType: "",
    };

    if (!employmentFormData.employmentStatus) {
      errors.employmentStatus =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_STATUS_REQUIRED;
    }
    if (!employmentFormData.occupationCode) {
      errors.occupationCode =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_OCCUPATION_REQUIRED;
    }
    if (!employmentFormData.employerPosition) {
      errors.employerPosition =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_POSITION_REQUIRED;
    }

    const selectedPosition = employmentAndIncomeReducer?.employerPositionCodes?.find(
      (pos) => pos.code === employmentFormData.employerPosition
    );
    if (
      selectedPosition?.description?.toLowerCase() === "other" &&
      !employmentFormData.jobTitle?.trim()
    ) {
      errors.jobTitle =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_JOB_TITLE_REQUIRED;
    }

    if (!employmentFormData.currentEmployer?.trim()) {
      errors.currentEmployer =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_EMPLOYER_NAME_REQUIRED;
    }
    if (!employmentFormData.employerLine1?.trim()) {
      errors.employerLine1 =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_ADDRESS_REQUIRED;
    }
    if (!employmentFormData.employerCity?.trim()) {
      errors.employerCity =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_CITY_REQUIRED;
    }
    if (!employmentFormData.employerProv?.trim()) {
      errors.employerProv =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_PROVINCE_REQUIRED;
    }
    if (!employmentFormData.employerPc?.trim()) {
      errors.employerPc =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_POSTAL_REQUIRED;
    } else if (!isValidPostalCode(employmentFormData.employerPc, "CAN")) {
      errors.employerPc =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_POSTAL_INVALID;
    }
    if (!employmentFormData.employerPhone?.trim()) {
      errors.employerPhone =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_PHONE_REQUIRED;
    } else if (!isValidPhoneNumber(employmentFormData.employerPhone)) {
      errors.employerPhone =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_PHONE_INVALID;
    }
    if (!employmentFormData.incomeType) {
      errors.incomeType =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_INCOME_SOURCE_REQUIRED;
    }

    const salaryValue = employmentFormData.employerSalary?.trim() || "0";
    if (isNaN(salaryValue) || parseFloat(salaryValue) < 0) {
      errors.employerSalary =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_EMPLOYMENT_DETAILS_MODAL.ERROR_SALARY_INVALID;
    }

    setEmploymentFormErrors(errors);

    const hasErrors = Object.values(errors).some((error) => error !== "");
    if (!hasErrors) {
      const dataToSave = { ...employmentFormData };
      if (!dataToSave.employerSalary?.trim()) dataToSave.employerSalary = "0";

      if (
        selectedPosition?.description?.toLowerCase() !== "other" &&
        selectedPosition
      ) {
        dataToSave.jobTitle = selectedPosition.description;
      }

      handleEmploymentSaveClick(dataToSave);
      handleEmploymentModalClose();
    }
  };

  return (
    <Modal
      show={showEmploymentEditModal}
      onHide={handleEmploymentModalClose}
      className="[ popup-modal__container ]"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="[ popup-modal__header ]" closeButton>
        <Modal.Title>
          {
            STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
              .EDIT_EMPLOYMENT_DETAILS_MODAL.TITLE
          }
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="[ row ]">
          <div className="[ col-12 ]">
            <div className="[ mb-3 ]">
              <label htmlFor="employmentStatus" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_EMPLOYMENT_DETAILS_MODAL.EMPLOYMENT_STATUS_LABEL
                }
              </label>
              <select
                id="employmentStatus"
                name="employmentStatus"
                className={`[ form-control ] ${
                  employmentFormErrors.employmentStatus ? "[ is-invalid ]" : ""
                }`}
                value={employmentFormData.employmentStatus}
                onChange={handleEmploymentInputChange}
              >
                <option value="">
                  {
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_EMPLOYMENT_DETAILS_MODAL.SELECT_OPTION
                  }
                </option>
                {(employmentAndIncomeReducer?.statusList || []).map(
                  (status) => (
                    <option key={status.code} value={status.code}>
                      {status.description}
                    </option>
                  )
                )}
              </select>
              {employmentFormErrors.employmentStatus && (
                <div className="[ invalid-feedback ]">
                  {employmentFormErrors.employmentStatus}
                </div>
              )}
            </div>

            <div className="[ mb-3 ]">
              <label htmlFor="occupationCode" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_EMPLOYMENT_DETAILS_MODAL.OCCUPATION_LABEL
                }
              </label>
              <select
                id="occupationCode"
                name="occupationCode"
                className={`[ form-control ] ${
                  employmentFormErrors.occupationCode ? "[ is-invalid ]" : ""
                }`}
                value={employmentFormData.occupationCode}
                onChange={handleEmploymentInputChange}
              >
                <option value="">
                  {
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_EMPLOYMENT_DETAILS_MODAL.SELECT_OPTION
                  }
                </option>
                {(Array.isArray(occupationCodes) ? occupationCodes : []).map(
                  (occ) => (
                    <option key={occ.code} value={occ.code}>
                      {occ.description}
                    </option>
                  )
                )}
              </select>
              {employmentFormErrors.occupationCode && (
                <div className="[ invalid-feedback ]">
                  {employmentFormErrors.occupationCode}
                </div>
              )}
            </div>

            <div className="[ mb-3 ]">
              <label htmlFor="employerPosition" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_EMPLOYMENT_DETAILS_MODAL.POSITION_LABEL
                }
              </label>
              <select
                id="employerPosition"
                name="employerPosition"
                className={`[ form-control ] ${
                  employmentFormErrors.employerPosition ? "[ is-invalid ]" : ""
                }`}
                value={employmentFormData.employerPosition}
                onChange={handleEmploymentInputChange}
              >
                <option value="">
                  {
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_EMPLOYMENT_DETAILS_MODAL.SELECT_OPTION
                  }
                </option>
                {(employmentAndIncomeReducer?.employerPositionCodes || []).map(
                  (pos) => (
                    <option key={pos.code} value={pos.code}>
                      {pos.description}
                    </option>
                  )
                )}
              </select>
              {employmentFormErrors.employerPosition && (
                <div className="[ invalid-feedback ]">
                  {employmentFormErrors.employerPosition}
                </div>
              )}
            </div>

            {/* Conditional "Other" field */}
            {(employmentAndIncomeReducer?.employerPositionCodes || [])
              .find((p) => p.code === employmentFormData.employerPosition)
              ?.description?.toLowerCase() === "other" && (
              <div className="[ mb-3 ]">
                <label htmlFor="jobTitle" className="[ form-label ]">
                  {
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_EMPLOYMENT_DETAILS_MODAL.JOB_TITLE_LABEL
                  }
                </label>
                <input
                  id="jobTitle"
                  type="text"
                  name="jobTitle"
                  className={`[ form-control ] ${
                    employmentFormErrors.jobTitle ? "[ is-invalid ]" : ""
                  }`}
                  value={employmentFormData.jobTitle}
                  onChange={handleEmploymentInputChange}
                  placeholder={
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_EMPLOYMENT_DETAILS_MODAL.JOB_TITLE_PLACEHOLDER
                  }
                />
                {employmentFormErrors.jobTitle && (
                  <div className="[ invalid-feedback ]">
                    {employmentFormErrors.jobTitle}
                  </div>
                )}
              </div>
            )}

            <div className="[ mb-3 ]">
              <label htmlFor="currentEmployer" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_EMPLOYMENT_DETAILS_MODAL.EMPLOYER_NAME_LABEL
                }
              </label>
              <input
                id="currentEmployer"
                type="text"
                name="currentEmployer"
                className={`[ form-control ] ${
                  employmentFormErrors.currentEmployer ? "[ is-invalid ]" : ""
                }`}
                value={employmentFormData.currentEmployer}
                onChange={handleEmploymentInputChange}
              />
              {employmentFormErrors.currentEmployer && (
                <div className="[ invalid-feedback ]">
                  {employmentFormErrors.currentEmployer}
                </div>
              )}
            </div>

            <div className="[ mb-3 ]">
              <label htmlFor="employerLine1" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_EMPLOYMENT_DETAILS_MODAL.EMPLOYER_ADDRESS_LABEL
                }
              </label>
              <input
                id="employerLine1"
                type="text"
                name="employerLine1"
                className={`[ form-control ] ${
                  employmentFormErrors.employerLine1 ? "[ is-invalid ]" : ""
                }`}
                value={employmentFormData.employerLine1}
                onChange={handleEmploymentInputChange}
              />
              {employmentFormErrors.employerLine1 && (
                <div className="[ invalid-feedback ]">
                  {employmentFormErrors.employerLine1}
                </div>
              )}
            </div>

            <div className="[ mb-3 ]">
              <label htmlFor="employerCity" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_EMPLOYMENT_DETAILS_MODAL.CITY_LABEL
                }
              </label>
              <input
                id="employerCity"
                type="text"
                name="employerCity"
                className={`[ form-control ] ${
                  employmentFormErrors.employerCity ? "[ is-invalid ]" : ""
                }`}
                value={employmentFormData.employerCity}
                onChange={handleEmploymentInputChange}
              />
              {employmentFormErrors.employerCity && (
                <div className="[ invalid-feedback ]">
                  {employmentFormErrors.employerCity}
                </div>
              )}
            </div>

            <div className="[ row ]">
              <div className="[ col-6 mb-3 ]">
                <label htmlFor="employerProv" className="[ form-label ]">
                  {
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_EMPLOYMENT_DETAILS_MODAL.PROVINCE_LABEL
                  }
                </label>
                <select
                  id="employerProv"
                  name="employerProv"
                  className={`[ form-control ] ${
                    employmentFormErrors.employerProv ? "[ is-invalid ]" : ""
                  }`}
                  value={employmentFormData.employerProv}
                  onChange={handleEmploymentInputChange}
                >
                  <option value="">
                    {
                      STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                        .EDIT_EMPLOYMENT_DETAILS_MODAL.SELECT_OPTION
                    }
                  </option>
                  {(Array.isArray(provinceList) ? provinceList : [])
                    .filter((p) => p.country === "CAN")
                    .map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.description}
                      </option>
                    ))}
                </select>
                {employmentFormErrors.employerProv && (
                  <div className="[ invalid-feedback ]">
                    {employmentFormErrors.employerProv}
                  </div>
                )}
              </div>
              <div className="[ col-6 mb-3 ]">
                <label htmlFor="employerPc" className="[ form-label ]">
                  {
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_EMPLOYMENT_DETAILS_MODAL.POSTAL_LABEL
                  }
                </label>
                <input
                  id="employerPc"
                  type="text"
                  name="employerPc"
                  className={`[ form-control ] ${
                    employmentFormErrors.employerPc ? "[ is-invalid ]" : ""
                  }`}
                  value={employmentFormData.employerPc}
                  onChange={handleEmploymentInputChange}
                  placeholder={
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_EMPLOYMENT_DETAILS_MODAL.POSTAL_PLACEHOLDER
                  }
                />
                {employmentFormErrors.employerPc && (
                  <div className="[ invalid-feedback ]">
                    {employmentFormErrors.employerPc}
                  </div>
                )}
              </div>
            </div>

            <div className="[ mb-3 ]">
              <label htmlFor="employerPhone" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_EMPLOYMENT_DETAILS_MODAL.EMPLOYER_PHONE_LABEL
                }
              </label>
              <input
                id="employerPhone"
                type="tel"
                name="employerPhone"
                className={`[ form-control ] ${
                  employmentFormErrors.employerPhone ? "[ is-invalid ]" : ""
                }`}
                value={formatPhoneNumber(employmentFormData.employerPhone)}
                onChange={handleEmploymentInputChange}
                placeholder={
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_EMPLOYMENT_DETAILS_MODAL.PHONE_PLACEHOLDER
                }
              />
              {employmentFormErrors.employerPhone && (
                <div className="[ invalid-feedback ]">
                  {employmentFormErrors.employerPhone}
                </div>
              )}
            </div>

            <div className="[ mb-3 ]">
              <label htmlFor="employerSalary" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_EMPLOYMENT_DETAILS_MODAL.SALARY_LABEL
                }
              </label>
              <input
                id="employerSalary"
                type="text"
                name="employerSalary"
                className={`[ form-control ] ${
                  employmentFormErrors.employerSalary ? "[ is-invalid ]" : ""
                }`}
                value={employmentFormData.employerSalary}
                onChange={handleEmploymentInputChange}
                placeholder={
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_EMPLOYMENT_DETAILS_MODAL.SALARY_PLACEHOLDER
                }
              />
              {employmentFormErrors.employerSalary && (
                <div className="[ invalid-feedback ]">
                  {employmentFormErrors.employerSalary}
                </div>
              )}
            </div>

            {/* Income Source — safe */}
            <div className="[ mb-3 ]">
              <label htmlFor="incomeType" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_EMPLOYMENT_DETAILS_MODAL.INCOME_SOURCE_LABEL
                }
              </label>
              <select
                id="incomeType"
                name="incomeType"
                className={`[ form-control ] ${
                  employmentFormErrors.incomeType ? "[ is-invalid ]" : ""
                }`}
                value={employmentFormData.incomeType}
                onChange={handleEmploymentInputChange}
              >
                <option value="">
                  {
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_EMPLOYMENT_DETAILS_MODAL.SELECT_OPTION
                  }
                </option>
                {(employmentAndIncomeReducer?.incomeSourcesList || []).map(
                  (source) => (
                    <option key={source.code} value={source.code}>
                      {source.description}
                    </option>
                  )
                )}
              </select>
              {employmentFormErrors.incomeType && (
                <div className="[ invalid-feedback ]">
                  {employmentFormErrors.incomeType}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="[ divider ]"></div>
      </Modal.Body>

      <Modal.Footer className="[ popup-modal-footer ] [ d-block ]">
        <div className="[ mb-3 ]">
          <button
            type="button"
            className="[ submit-btn btn ] [ w-100 ]"
            onClick={handleSave}
            style={{ borderRadius: ".5em" }}
          >
            {
              STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                .EDIT_EMPLOYMENT_DETAILS_MODAL.SAVE_BUTTON
            }
          </button>
        </div>
        <div className="[ mb-3 text-center ]">
          <span
            className="[ link ]"
            onClick={handleEmploymentModalClose}
            role="presentation"
            style={{
              textDecoration: "underline",
              color: "#0090c6",
              cursor: "pointer",
            }}
          >
            {
              STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                .EDIT_EMPLOYMENT_DETAILS_MODAL.CANCEL_BUTTON
            }
          </span>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
